/**
 * @file look-summary.ts
 * @description Сводки свёрнутых групп вкладки «Оформление» секции
 * (phase19.16 Р2; uiux-design-r2 §2). SB120 этап 1 (uiux-sb120-stage1 §2):
 * числами — «1280 · 100 %, тел. 80 %», «0/0 · 96 · бока 24 · 30 %»,
 * «2 · 7/12 + 5/12 · 32 px», «Фото · 30 % · ⌖ телефон», «сначала 2 · везде».
 * Сводка обрезается после 40 знаков (`clipSummary`), полностью — в `title`.
 */

import {
  DEFAULT_SITE_THEME,
  SITE_LIMITS,
  SITE_THEME_LIMITS,
  spaceStepPx,
  type SectionColumns,
  type SectionHeight,
  type SectionLook,
  type SiteSpacingDensity,
  type SiteTheme,
} from "@chaiberi/shared";

import { sharesLabel } from "./column-shares";
import { formatAspect } from "./look-size";
import { isOutsideFrame } from "./look-outside";
import { formatPx } from "./look-space";
import { BG_ROLE_LABELS } from "./section-meta";

/** Длина сводки до обрезки (uiux §2). */
export const SUMMARY_MAX = 40;

/** Обрезать сводку после 40 знаков с «…». */
export function clipSummary(text: string): string {
  return text.length > SUMMARY_MAX ? `${text.slice(0, SUMMARY_MAX - 1).trimEnd()}…` : text;
}

export const VALIGN_LABELS: Readonly<Record<SectionColumns["valign"], string>> = {
  top: "Сверху",
  center: "По центру",
  bottom: "Снизу",
  stretch: "Растянуть",
};

export function bgSummary(bg: SectionLook["bg"]): string {
  if (bg.kind === "transparent") return "Прозрачный";
  if (bg.kind === "role") return `Цвет: ${BG_ROLE_LABELS[bg.role]}`;
  // Свой цвет (решение 2026-10-11, SB120).
  if (bg.kind === "custom") return `Свой цвет ${bg.color}`;
  const phone = bg.focusPhone || bg.mediaIdPhone || bg.zoomPhone ? " · ⌖ телефон" : "";
  return `Фото · ${Math.round(bg.dim * 100)} %${phone}`;
}

/** Ширина: «Во всю», «1280», «768». */
export function widthLabel(container: SectionLook["container"]): string {
  return container === "full" ? "Во всю" : String(container ?? 1280);
}

/** Привычные пропорции словами «4:5», «16:9»; иначе — число «1,25». */
const ASPECT_NAMES: ReadonlyArray<[number, string]> = [
  [0.8, "4:5"],
  [0.75, "3:4"],
  [1, "1:1"],
  [1.333, "4:3"],
  [1.5, "3:2"],
  [1.778, "16:9"],
];

export function aspectName(aspect: number): string {
  return ASPECT_NAMES.find(([v]) => Math.abs(v - aspect) < 0.01)?.[1] ?? formatAspect(aspect);
}

/** Высота коротко: «по содержимому», «по фото», «кадр 4:5», «600 px», «100 %». */
export function heightLabel(height: SectionHeight): string {
  switch (height.kind) {
    case "content":
      return "по содержимому";
    case "photo":
      return height.aspect === undefined ? "по фото" : `кадр ${aspectName(height.aspect)}`;
    case "number":
      return `${formatPx(height.value)} px`;
    case "screen":
      return `${formatPx(height.value)} %`;
  }
}

/** Сводка «Ширины и высоты»; `frame` — рамка сайта: «· за рамку» только при фактическом выходе. */
export function sizeSummary(look: SectionLook, frame: Pick<SiteTheme["frame"], "mode"> = DEFAULT_SITE_THEME.frame): string {
  const phone = look.heightPhone ? `, тел. ${heightLabel(look.heightPhone)}` : "";
  const card = look.card.on ? " · подложка" : "";
  // SB120 этап 2: «Фон за рамку сайта» видно и у свёрнутой группы — если фон правда выходит.
  const outside = isOutsideFrame(look, frame) ? " · за рамку" : "";
  return `${widthLabel(look.container)} · ${heightLabel(look.height)}${phone}${card}${outside}`;
}

export function columnsSummary(columns: SectionColumns, density: SiteSpacingDensity = SITE_THEME_LIMITS.density.default): string {
  const n = columns.items.length;
  const shares = n > 1 ? ` · ${sharesLabel(columns.shares)}` : "";
  const gap = n > 1 ? ` · ${formatPx(columns.gap ?? spaceStepPx("m", density))} px` : "";
  return `${n}${shares}${gap}`;
}

/** Пара значений: «96» (одинаково), «96/64»; «как у сайта» — «сайт». */
function pair(a: number | undefined, b: number | undefined): string {
  const f = (v: number | undefined) => (v === undefined ? "сайт" : formatPx(v));
  return a === b ? f(a) : `${f(a)}/${f(b)}`;
}

/** «как у сайта» — пока ничего не трогали; иначе числа: «0/0 · 96 · бока 24 · 30 %». */
export function spaceSummary(space: SectionLook["space"]): string {
  const parts: string[] = [];
  const { outerTop, outerBottom, innerTop, innerBottom, innerLeft, innerRight, phoneFactor, overlap, mergeWithPrev } = space;
  if (outerTop !== undefined || outerBottom !== undefined) parts.push(`${f2(outerTop)}/${f2(outerBottom)}`);
  if (innerTop !== undefined || innerBottom !== undefined) parts.push(pair(innerTop, innerBottom));
  if (innerLeft !== undefined || innerRight !== undefined) parts.push(`бока ${pair(innerLeft, innerRight)}`);
  if (phoneFactor !== undefined) parts.push(`${Math.round(phoneFactor * 100)} %`);
  if (overlap > 0) parts.push(`наезд ${formatPx(overlap)}`);
  if (mergeWithPrev) parts.push("слит с блоком выше");
  return parts.length === 0 ? "как у сайта" : parts.join(" · ");
}

function f2(v: number | undefined): string {
  return v === undefined ? "сайт" : formatPx(v);
}

export function decorSummary(decor: SectionLook["decor"]): string {
  return decor.length === 0 ? "нет" : `${decor.length} из ${SITE_LIMITS.sectionDecor}`;
}

export function phoneSummary(look: SectionLook, columns: SectionColumns): string {
  const order = columns.items.length > 1 && columns.phoneOrder ? `сначала ${columns.phoneOrder[0]} · ` : "";
  const v = look.visibility;
  const where = v.phone && v.tablet && v.desktop ? "везде" : !v.phone && !v.tablet && !v.desktop ? "нигде" : v.phone ? "не везде" : "без телефона";
  return `${order}${where}`;
}

/**
 * Прежний ключ памяти «Оформления» секции (одна открытая группа, до SB122).
 * Читается один раз как запасной вариант и переносится в память групп.
 */
export const LOOK_GROUP_STORAGE_KEY = "commora.section-look.group";

/**
 * Перенос прежней памяти «Оформления» в память групп (SB122;
 * uiux-settings-groups §1): если записи `newKey`
 * (`commora.settings-groups.section-look`) ещё нет, а старая есть —
 * последняя открытая группа становится `last` (до 768 откроется она), старый
 * ключ удаляется. Хранилище недоступно — молча пропускаем: память —
 * удобство, без неё всё работает.
 */
export function migrateLookGroupMemory(newKey: string): void {
  try {
    const legacy = localStorage.getItem(LOOK_GROUP_STORAGE_KEY);
    if (legacy === null) return;
    if (localStorage.getItem(newKey) === null) {
      localStorage.setItem(newKey, JSON.stringify({ collapsed: [], expanded: [], last: legacy }));
    }
    localStorage.removeItem(LOOK_GROUP_STORAGE_KEY);
  } catch {
    // Хранилище недоступно — панель просто не помнит.
  }
}
